import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  CalendarDays,
  Users,
  Gem,
  MapPin,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  ArrowRight,
  FileText,
  ChevronRight,
  Clock,
  MessageCircle,
  Image as ImageIcon,
} from 'lucide-react';
import CreatorSubmissionPanel from './CreatorSubmissionPanel';
import CreatorPerformanceCard from './CreatorPerformanceCard';
import CreatorCollabJourney from './CreatorCollabJourney';
import { beginInstagramConnect, consumeInstagramConnectionConfirmation } from '../../utils/instagramConnect';
import {
  checkCreatorVerification,
  getCreatorJourneyStatus,
  getFeedOfferDetail,
  getCreatorWallet,
  createApplication,
  respondToInvitation,
  confirmCreatorApplicationCompletion,
} from '../../services/api';
import Button from '../common/design-system/Button';
import { Badge } from '../common/design-system/Badge';
import Skeleton from '../common/design-system/Skeleton';
import RichTextDisplay, { TermsDisplay } from '../common/RichTextDisplay';
import { useAuth } from '../auth/AuthContext';
import { announcePwaInstallOpportunity } from '../../utils/pwa';
import styles from './CreatorOfferView.module.scss';
import PaidCollabPaymentStatus from '../payments/PaidCollabPaymentStatus';
import { track } from '../../services/measurement';
import ImageWithFallback from '../common/ImageWithFallback';
import Avatar from '../common/Avatar';

const stripHtml = (value = '') => (
  String(value)
    .replace(/<style[^>]*>[^]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[^]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim()
);

const hasRenderableTerms = (terms) => {
  const text = stripHtml(terms).toLowerCase();
  return !['', 'na', 'n/a', 'none', 'null', 'undefined'].includes(text);
};

const CREATOR_JOURNEY_SYNC_FIELDS = [
  'application_status',
  'invitation_status',
  'has_applied',
  'is_winner',
  'invitation_id',
  'approval_status',
  'delivery_confirmed',
  'business_review_deadline',
];
const CREATOR_JOURNEY_STATUS_POLL_MS = 30000;

const hasCreatorJourneyUpdate = (currentOffer, nextOffer) => CREATOR_JOURNEY_SYNC_FIELDS.some(
  field => currentOffer?.[field] !== nextOffer?.[field],
);

const CreatorOfferView = () => {
  const { t } = useTranslation('collab');
  const { currentUser, refreshUser } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const reactLocation = useLocation();
  const deliverablesRef = useRef(null);
  const performanceRef = useRef(null);
  const briefRef = useRef(null);

  // States
  const [offer, setOffer] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [instagramConfirmation, setInstagramConfirmation] = useState(null);
  const [creatorWallet, setCreatorWallet] = useState(null);
  const [deliverablesProgress, setDeliverablesProgress] = useState(null);
  const [creatorPerformance, setCreatorPerformance] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(true);
  const [confirmingCompletion, setConfirmingCompletion] = useState(false);

  const handlePerformanceUpdate = useCallback((perf) => {
    setCreatorPerformance(perf);
    setPerformanceLoading(false);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [offerRes, vStatus] = await Promise.all([
          getFeedOfferDetail(id),
          checkCreatorVerification()
        ]);

        setOffer(offerRes.data);
        setVerificationStatus(vStatus.data);

      } catch (err) {
        console.error('Error loading offer:', err);
        setError(err.response?.data?.detail || t('view.load_error'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, t]);

  // Application approval and invitation decisions happen from the business
  // side. Keep the creator journey in sync so its next-step controls unlock
  // without requiring a full page refresh.
  useEffect(() => {
    if (!offer
      || ['completed', 'rejected', 'cancelled'].includes(offer.application_status)
      || ['declined', 'revoked', 'expired'].includes(offer.invitation_status)) return undefined;
    if (!offer.has_applied && !offer.invitation_id) return undefined;

    let cancelled = false;
    let timeoutId;
    const pollOfferStatus = async () => {
      try {
        const response = await getCreatorJourneyStatus(id);
        if (!cancelled && hasCreatorJourneyUpdate(offer, response.data)) {
          setOffer(currentOffer => hasCreatorJourneyUpdate(currentOffer, response.data)
            ? { ...currentOffer, ...response.data }
            : currentOffer);
        }
      } catch {
        // The initial load already owns the visible error state. A transient
        // polling failure should not interrupt an otherwise usable journey.
      } finally {
        if (!cancelled) timeoutId = window.setTimeout(pollOfferStatus, CREATOR_JOURNEY_STATUS_POLL_MS);
      }
    };

    timeoutId = window.setTimeout(pollOfferStatus, CREATOR_JOURNEY_STATUS_POLL_MS);
    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [id, offer]);

  useEffect(() => {
    if (!offer || !['paid', 'hybrid'].includes(offer.compensation_mode)) return;
    getCreatorWallet().then((response) => setCreatorWallet(response.data)).catch(() => setCreatorWallet(null));
  }, [offer]);

  useEffect(() => {
    const confirmation = consumeInstagramConnectionConfirmation(`${reactLocation.pathname}${reactLocation.search || ''}`)
      || consumeInstagramConnectionConfirmation(reactLocation.pathname);

    if (!confirmation) return;

    setInstagramConfirmation(confirmation);
    refreshUser?.();
    toast.success(t('view.instagram_connected_toast', 'Instagram connected. Your content is ready for reporting.'));
  }, [reactLocation.pathname, reactLocation.search, refreshUser, t]);

  const handleApply = async () => {
    if (offer?.is_full) {
      toast.info(t('view.collab_full'));
      return;
    }
    try {
      setApplying(true);
      const res = await createApplication(id);
      const missingPayoutSetup = ['paid', 'hybrid'].includes(offer.compensation_mode) && !creatorWallet?.payout_setup_ready;
      track('application_submitted', { flow: offer.compensation_mode || 'exchange' });
      if (missingPayoutSetup) track('paid_collab_applied_without_payout_setup', { flow: offer.compensation_mode });

      toast.success(t('view.apply_success'));
      announcePwaInstallOpportunity('creator_applied_to_collab');
      setOffer(prev => ({
        ...prev,
        has_applied: true,
        application_status: res.data.application?.status || 'applied',
        applications_count: res.data.applications_count ?? ((prev.applications_count || 0) + 1)
      }));
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.error?.includes('verificada')) {
        toast.error(t('view.requires_verification'));
        navigate('/complete-profile', { state: { returnTo: reactLocation.pathname } });
        return;
      }
      toast.error(err.response?.data?.error || t('view.apply_error'));
    } finally {
      setApplying(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!offer.invitation_id) return;
    try {
      setApplying(true);
      await respondToInvitation(offer.invitation_id, 'accept');
      track('collab_invitation_accepted', { flow: offer.compensation_mode || 'exchange' });
      if (['paid', 'hybrid'].includes(offer.compensation_mode) && !creatorWallet?.payout_setup_ready) {
        track('paid_collab_accepted_without_payout_setup', { flow: offer.compensation_mode });
      }
      toast.success(t('view.accept_success'));
      announcePwaInstallOpportunity('creator_accepted_invitation');
      setOffer(prev => ({
        ...prev,
        has_applied: true,
        invitation_status: 'accepted',
        application_status: 'in_progress',
      }));
      if (reactLocation.pathname !== `/creator/collabs/${id}`) {
        navigate(`/creator/collabs/${id}`, { replace: true });
      }
      setTimeout(() => {
        scrollToDeliverables();
      }, 300);
    } catch (err) {
      toast.error(t('view.accept_error'));
    } finally {
      setApplying(false);
    }
  };

  const handleDeclineInvitation = async () => {
    if (!offer.invitation_id) return;
    try {
      setApplying(true);
      await respondToInvitation(offer.invitation_id, 'decline');
      toast.info(t('view.decline_info'));
      navigate(hasInvitedAccess && !isApprovedCreator ? '/creator/invited' : '/creator/feed');
    } catch (err) {
      toast.error(t('view.decline_error'));
    } finally {
      setApplying(false);
    }
  };

  const handleCreatorConfirmCompletion = async () => {
    if (!offer?.application_id || !isAccepted) return;
    try {
      setConfirmingCompletion(true);
      const response = await confirmCreatorApplicationCompletion(offer.application_id);
      setOffer(prev => ({
        ...prev,
        application_status: response.data.application?.status || (isPaidCollab ? 'accepted' : 'completed'),
        is_winner: true,
        has_applied: true,
        delivery_confirmed: true,
        business_review_deadline: response.data.business_review_deadline ?? prev?.business_review_deadline,
        approval_status: response.data.approval_status || (isPaidCollab ? 'under_business_review' : 'completed'),
      }));
      toast.success(response.data.message || t('view.creator_completion_success', 'Tu colaboración fue marcada como completada.'));
    } catch (err) {
      if (err.response?.data?.code === 'already_under_review') {
        setOffer(prev => ({
          ...prev,
          delivery_confirmed: true,
          approval_status: 'under_business_review',
        }));
        toast.info(err.response?.data?.error || t('creatorJourney.review.under_review_warning_title', 'Tus entregas ya están confirmadas y bajo revisión del negocio.'));
      } else {
        toast.error(err.response?.data?.error || t('view.creator_completion_error', 'No pudimos confirmar la finalización.'));
      }
    } finally {
      setConfirmingCompletion(false);
    }
  };

  const handleInstagramConnect = () => {

    if (!currentUser) {
      navigate('/login', { state: { returnTo: reactLocation.pathname } });
      return;
    }
    beginInstagramConnect(`${reactLocation.pathname}${reactLocation.search || ''}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const lang = localStorage.getItem('i18nextLng') || 'es';
    const localeMap = { 'en': 'en-US', 'es': 'es-ES', 'pt': 'pt-BR' };
    return new Date(dateString).toLocaleDateString(localeMap[lang] || 'es-ES', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) return (
    <div className={styles['creator-panel']}>
      <div className={styles.container}><Skeleton height="600px" /></div>
    </div>
  );

  if (error || !offer) return (
    <div className={styles['creator-panel']}>
      <div className={styles.container}>
        <div className={styles['empty-state']}>
          <AlertTriangle size={48} />
          <h2>{t('view.not_found')}</h2>
          <p>{error}</p>
          <Button variant="primary" onClick={() => navigate('/creator/feed')}>{t('view.back')}</Button>
        </div>
      </div>
    </div>
  );

  const hasApplied = !!offer.has_applied;
  const isFull = Boolean(offer.is_full || offer.availability_status === 'full' || (offer.quantity > 0 && offer.remaining_slots === 0));
  const applicationStatus = offer.application_status || null;
  const isAccepted = ['accepted', 'approved', 'completed', 'in_progress', 'active_collaboration', 'winner'].includes(applicationStatus) || offer.invitation_status === 'accepted' || !!offer.is_winner;
  const isRejected = applicationStatus === 'rejected' || applicationStatus === 'cancelled';
  const currentUserInstagramConnected = currentUser?.creator_profile?.instagram_account_connected;
  const isInstagramConnected = currentUserInstagramConnected == null
    ? !!verificationStatus?.instagram_account_connected
    : !!currentUserInstagramConnected;
  const participationLabel = isAccepted
    ? t('view.sections.accepted_msg')
    : isRejected
      ? t('view.sections.rejected_msg', 'This application was not selected.')
      : t('view.applied_badge');
  const exchangeValue = Number(offer.exchange_value ?? offer.value ?? 0);
  const cashCompensation = Number(offer.cash_compensation ?? (offer.sponsor_this_offer ? offer.sponsor_amount : 0) ?? 0);
  const totalCreatorValue = Number(offer.total_creator_value ?? (exchangeValue + cashCompensation));
  const compensationCurrency = offer.cash_currency || 'USD';
  const compensationValue = `${compensationCurrency} ${totalCreatorValue.toLocaleString()}`;
  const isPaidCollab = (['paid', 'hybrid'].includes(offer.compensation_mode) || offer.sponsor_this_offer) && cashCompensation > 0;
  const payoutSetupIncomplete = isPaidCollab && creatorWallet && !creatorWallet.payout_setup_ready;
  const payoutReminderTitle = !hasApplied
    ? t('view.sections.payout_apply_title')
    : isAccepted
      ? t('view.sections.payout_active_title')
      : t('view.sections.payout_accept_title');
  const payoutReminderBody = !hasApplied
    ? t('view.sections.payout_apply_body')
    : isAccepted
      ? t('view.sections.payout_active_body')
      : t('view.sections.payout_accept_body');
  const payoutReminderCta = isAccepted
    ? t('view.sections.payout_complete_cta')
    : t('view.sections.payout_setup_cta');
  const openPayoutSetup = () => {
    track('payout_setup_cta_viewed', { flow: isAccepted ? 'active_collab' : 'paid_collab' });
    navigate('/creator/wallet');
  };
  const showTerms = hasRenderableTerms(offer.terms_and_conditions);
  const isOpenDeliverables = offer.deliverables_mode === 'open';
  const targetDeliverables = (() => {
    const counts = Object.values(offer.deliverables_counts || {})
      .map(value => Number(value) || 0)
      .reduce((sum, value) => sum + value, 0);
    if (counts > 0) return counts;
    if (Array.isArray(offer.exchange_options) && offer.exchange_options.length > 0) return offer.exchange_options.length;
    return 0;
  })();
  const effectiveSubmittedCount = deliverablesProgress?.submittedCount ?? 0;
  const effectiveTotalDeliverables = isOpenDeliverables
    ? null
    : (deliverablesProgress?.totalDeliverables ?? targetDeliverables);
  const isDeliverablesCompleted = isOpenDeliverables ? false : (deliverablesProgress?.isCompleted ?? false);
  const isDeliverablesReady = isOpenDeliverables
    ? (deliverablesProgress?.isMinimumSatisfied ?? effectiveSubmittedCount > 0)
    : isDeliverablesCompleted;
  const pendingDeliverablesCount = effectiveTotalDeliverables == null
    ? 0
    : Math.max(0, effectiveTotalDeliverables - effectiveSubmittedCount);

  const deliverablesSummary = isOpenDeliverables
    ? effectiveSubmittedCount > 0
      ? t('view.journey.open_deliverables_count', '{{count}} registradas', { count: effectiveSubmittedCount })
      : t('view.journey.open_deliverables_pending', 'Agregar contenido')
    : isDeliverablesCompleted
    ? t('view.sections.deliverables_completed_label', 'Completado')
    : pendingDeliverablesCount > 0
      ? t('view.sections.deliverables_pending_count', { count: pendingDeliverablesCount })
      : t('view.sections.deliverables_pending_generic', 'Pending');
  const invitedOfferIds = currentUser?.creator_profile?.invited_offer_ids || [];
  const isApprovedCreator = currentUser?.creator_profile?.admin_verified
    && currentUser?.creator_profile?.verification_status === 'approved';
  const hasInvitedAccess = currentUser?.creator_profile?.has_invited_access
    || invitedOfferIds.length > 0
    || currentUser?.creator_profile?.invited_collab;
  const scrollToDeliverables = () => {
    deliverablesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const openCreatorReport = () => navigate(`/creator/collabs/${id}/report`);
  const scrollToBrief = () => {
    briefRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const hasFloatingMobileBar = isAccepted && !isDeliverablesCompleted;
  const isInvitationPending = offer.has_invitation && ['pending', 'opened'].includes(offer.invitation_status);
  const journeyPhase = isRejected ? 'rejected' : isAccepted ? 'accepted' : hasApplied ? 'applicant' : 'guest';
  const journeyPrimaryAction = isAccepted
    ? isDeliverablesCompleted
      ? openCreatorReport
      : isInstagramConnected
        ? scrollToDeliverables
        : handleInstagramConnect
    : isInvitationPending
      ? handleAcceptInvitation
      : isRejected || hasApplied || isFull
        ? undefined
        : handleApply;
  const journeyPrimaryLabel = isAccepted
    ? isDeliverablesCompleted
      ? t('view.journey.view_results', 'Ver resultados')
      : isInstagramConnected
        ? t('view.journey.report_deliveries', 'Reportar entregas')
        : t('view.sections.btn_connect_ig')
    : isInvitationPending
      ? t('view.sections.btn_accept')
      : isRejected || hasApplied || isFull
        ? undefined
        : t('view.sections.btn_apply');
  const journeySecondaryAction = isInvitationPending
    ? handleDeclineInvitation
    : isAccepted
      ? scrollToBrief
      : undefined;
  const journeySecondaryLabel = isInvitationPending
    ? t('view.sections.btn_decline')
    : isAccepted
      ? t('view.journey.view_brief', 'Ver brief')
      : undefined;

  const useJourneyWorkspace = true;
  if (useJourneyWorkspace) return (
    <CreatorCollabJourney
      id={id}
      offer={offer}
      phase={journeyPhase}
      isInvitation={isInvitationPending}
      isFull={isFull}
      hasApplied={hasApplied}
      isAccepted={isAccepted}
      isRejected={isRejected}
      applicationStatus={applicationStatus}
      isInstagramConnected={isInstagramConnected}
      submittedCount={effectiveSubmittedCount}
      totalDeliverables={effectiveTotalDeliverables}
      submittedDeliverables={deliverablesProgress?.items || []}
      deliverablesMode={offer.deliverables_mode}
      isDeliverablesCompleted={isDeliverablesCompleted}
      isDeliverablesReady={isDeliverablesReady}
      performance={creatorPerformance}
      performanceLoading={performanceLoading}
      creatorWallet={creatorWallet}
      requiredDeliverables={offer.exchange_options?.map(opt => opt.code || opt.id || opt) || []}
      deliverablesCounts={offer.deliverables_counts || {}}
      onApply={handleApply}
      onAcceptInvitation={handleAcceptInvitation}
      onDeclineInvitation={handleDeclineInvitation}
      onCreatorConfirmCompletion={handleCreatorConfirmCompletion}
      onInstagramConnect={handleInstagramConnect}
      onDeliverablesProgress={setDeliverablesProgress}
      onPerformanceUpdate={handlePerformanceUpdate}
      onBack={() => navigate(hasInvitedAccess && !isApprovedCreator ? '/creator/invited' : '/creator/feed')}
      onPayoutSetup={openPayoutSetup}
      onOpenReport={openCreatorReport}
      onRefresh={() => window.location.reload()}
      applying={applying}
      confirmingCompletion={confirmingCompletion}
    />
  );

  return (
    <div className={`${styles['creator-panel']} ${hasFloatingMobileBar ? styles['has-floating-bar'] : ''}`}>
      <div className={styles.container}>
        <div className={styles['top-actions']}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(hasInvitedAccess && !isApprovedCreator ? '/creator/invited' : '/creator/feed')}
            className={styles['back-button']}
          >
            <ArrowLeft size={16} /> {t('view.back')}
          </Button>
        </div>

        <div className={styles['page-grid']}>
          {/* ── MAIN CONTENT ── */}
          <div className={styles['main-content']}>

            {/* Offer Hero Card */}
            <div className={styles['offer-card']}>
              <div className={styles['hero-image-box']}>
                <ImageWithFallback src={offer.featured_image_url} alt={offer.title} name={offer.title} />
              </div>
              <div className={styles['content-body']}>
                <div className={styles['badges-row']}>
                  <Badge variant="deliverable" className={styles['status-pill']}>{t('view.active_opp')}</Badge>
                  {offer.compensation_mode === 'paid' && (
                    <Badge variant="success" className={styles['status-pill']}>PAID COLLAB</Badge>
                  )}
                  {offer.compensation_mode === 'hybrid' && (
                    <Badge variant="warning" className={styles['status-pill']}>CASH + EXPERIENCE</Badge>
                  )}
                  {/* ── FIX 1: branded pill for business name ── */}
                  <div className={styles['business-tag']}>
                    <Avatar size="xs" src={offer.business_logo_url} name={offer.business_name} alt={`${offer.business_name} logo`} />
                    <span>{offer.business_name}</span>
                  </div>
                </div>
                <h1>{offer.title}</h1>
                <div className={styles['meta-row']}>
                  <div className={styles['meta-item']}><MapPin size={18} /> {offer.country_name || 'Global'}</div>
                  <div className={styles['meta-item']}><CalendarDays size={18} /> {formatDate(offer.from_date)} - {formatDate(offer.to_date)}</div>
                  <div className={styles['meta-item']}>
                    <Users size={18} /> {offer.applications_count || 0} {t(`view.${offer.applications_count === 1 ? 'applicant_one' : 'applicant_many'}`)}
                  </div>
                </div>
              </div>
            </div>

            {payoutSetupIncomplete && (
              <div className={styles['payout-reminder']} role="status">
                <AlertTriangle size={20} />
                <div className={styles['payout-reminder-copy']}>
                  <strong>{payoutReminderTitle}</strong>
                  <p>{payoutReminderBody}</p>
                </div>
                <Button variant="outline" onClick={openPayoutSetup}>{payoutReminderCta}</Button>
              </div>
            )}

            <CreatorCollabJourney
              phase={journeyPhase}
              isInvitation={isInvitationPending}
              isFull={isFull}
              isInstagramConnected={isInstagramConnected}
              submittedCount={effectiveSubmittedCount}
              totalDeliverables={effectiveTotalDeliverables}
              submittedDeliverables={deliverablesProgress?.items || []}
              deliverablesMode={offer.deliverables_mode}
              isDeliverablesCompleted={isDeliverablesCompleted}
              isDeliverablesReady={isDeliverablesReady}
              performance={creatorPerformance}
              compensationValue={compensationValue}
              onPrimaryAction={journeyPrimaryAction}
              primaryActionLabel={journeyPrimaryLabel}
              onSecondaryAction={journeySecondaryAction}
              secondaryActionLabel={journeySecondaryLabel}
              onOpenReport={openCreatorReport}
              primaryLoading={applying}
            />

            {isAccepted && !isDeliverablesCompleted && (
              <div className={styles['mobile-action-card']}>
                <div className={styles['mobile-action-summary']}>
                  <span>{t('view.sections.deliverables_mobile_label', 'Deliverables')}</span>
                  <strong>{deliverablesSummary}</strong>
                </div>
                <Button variant="primary" fullWidth onClick={isInstagramConnected ? scrollToDeliverables : handleInstagramConnect}>
                  {isInstagramConnected ? t('view.sections.btn_upload_content', 'Upload content') : t('view.sections.btn_connect_ig')} <ChevronRight size={18} />
                </Button>
              </div>
            )}

            {/* Submission Panel: only appears after the creator is accepted, preserving existing behavior. */}
            {hasApplied && isAccepted && (
              <div className={styles['accepted-submission-block']} ref={deliverablesRef}>
                {instagramConfirmation && isInstagramConnected && (
                  <div className={styles['instagram-confirmation-banner']}>
                    <CheckCircle2 size={18} />
                    <div>
                      <strong>{t('view.instagram_connected_title', 'Instagram connected')}</strong>
                      <p>{t('view.instagram_connected_body', 'FOTH confirmed your Instagram connection. You can now select content and attach it to this Collab report.')}</p>
                    </div>
                  </div>
                )}
                <CreatorSubmissionPanel
                  offerId={id}
                  isWinner={isAccepted}
                  isInstagramConnected={isInstagramConnected}
                  requiredDeliverables={offer.exchange_options?.map(opt => opt.code || opt.id || opt)}
                  deliverablesCounts={offer.deliverables_counts}
                  deliverablesMode={offer.deliverables_mode}
                  onInstagramConnect={handleInstagramConnect}
                  onDeliverablesProgress={setDeliverablesProgress}
                  onPerformanceUpdate={handlePerformanceUpdate}
                />
                <div ref={performanceRef} className={styles['mobile-performance-anchor']}>
                  <CreatorPerformanceCard
                    performance={creatorPerformance}
                    loading={performanceLoading}
                  />
                </div>
              </div>
            )}

            <article ref={briefRef} className={`${styles['info-card']} ${styles['description-card']}`}>
                <h4><FileText size={20} /> {t('view.sections.summary')}</h4>
                <div className={styles['rich-content']}>
                  <RichTextDisplay content={offer.description} />
                </div>
            </article>

            {showTerms && (
            <section className={`${styles['info-card']} ${styles['terms-card']}`}>
              <h4><Sparkles size={20} /> {t('view.sections.terms')}</h4>
              <TermsDisplay content={offer.terms_and_conditions} />
            </section>
            )}

            {Array.isArray(offer.additional_images) && offer.additional_images.length > 0 && (
              <section className={`${styles['info-card']} ${styles['gallery-card'] || ''}`}>
                <h4><ImageIcon size={20} /> {t('view.sections.gallery', 'Galería de imágenes')}</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px', marginTop: '12px' }}>
                  {offer.additional_images.map((img, idx) => {
                    const src = typeof img === 'string' ? img : (img?.url || img?.image || img?.file);
                    if (!src) return null;
                    return (
                      <div key={idx} style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0', aspectRatio: '1/1' }}>
                        <img src={src} alt={`Imagen ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

          </div>

          {/* ── SIDEBAR ── */}
          <aside className={styles.sidebar}>

            {/* Participation status card */}
            <div className={styles['status-sidebar-card']}>
              <header className={styles['sidebar-header']}>
                <div className={styles.count}>
                  <Users size={16} /> {offer.applications_count || 0} {t(`view.${offer.applications_count === 1 ? 'applicant_one' : 'applicant_many'}`)}
                </div>
                <h3>{isAccepted ? t('view.sections.accepted_title', 'Collaboration accepted') : t('view.sections.participation')}</h3>
              </header>

              <div className={styles['status-content']}>
                {hasApplied ? (
                  <div className={styles['status-alert']}>
                    <CheckCircle2 size={16} />
                    {participationLabel}
                  </div>
                ) : (
                  <div className={styles['participation-copy']}>
                    <p>
                      {t('view.sections.exclusive_opp')}
                    </p>
                  </div>
                )}

                {!hasApplied ? (
                  isFull ? (
                    <div className={styles['status-alert']}>
                      {t('view.collab_full')}
                    </div>
                  ) : offer.has_invitation && offer.invitation_status === 'pending' ? (
                    <div className={styles['sidebar-action-stack']}>
                      <div className={`${styles['status-alert']} ${styles['is-invitation']}`}>
                        <Sparkles size={16} /> {t('view.sections.invitation_title')}
                      </div>
                      <Button
                        variant="primary"
                        fullWidth
                        onClick={handleAcceptInvitation}
                        loading={applying}
                      >
                        {t('view.sections.btn_accept')} <CheckCircle2 size={18} />
                      </Button>
                      <Button
                        variant="outline"
                        fullWidth
                        onClick={handleDeclineInvitation}
                        disabled={applying}
                      >
                        {t('view.sections.btn_decline')}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={handleApply}
                      loading={applying}
                    >
                      {t('view.sections.btn_apply')} <ChevronRight size={18} />
                    </Button>
                  )
                ) : (
                  /* ── luxury "Next Step" waiting card ── */
                  <div className={styles['sidebar-action-stack']}>
                    <div className={styles['next-step-waiting']}>
                      <div className={styles['waiting-label']}>
                        {isDeliverablesCompleted ? <CheckCircle2 size={13} /> : <Clock size={13} />}
                        {isDeliverablesCompleted
                          ? t('view.sections.deliverables_completed_label', 'Completado')
                          : t('view.sections.next_step')}
                      </div>
                      <p className={styles['waiting-body']}>
                        {isDeliverablesCompleted
                          ? t('view.sections.deliverables_completed_body', 'Has enviado todos tus entregables. La marca y FOTH revisarán las métricas certificadas.')
                          : t('view.sections.accepted_next_step', 'Attach your posts, stories or reels from Instagram to complete your deliverables.')}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      fullWidth
                      onClick={() => navigate(hasInvitedAccess && !isApprovedCreator ? '/creator/invited' : '/creator/feed')}
                    >
                      {t('view.back')}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Instagram connect nudge */}
            {hasApplied && isAccepted && !isInstagramConnected && (
              <div className={styles['next-step-card']}>
                <div className={styles['badge-step']}>
                  <Sparkles size={12} /> {t('view.sections.optimization')}
                </div>
                <h3>{t('view.sections.auto_report')}</h3>
                <p>{t('view.sections.auto_report_desc')}</p>

                <Button
                  fullWidth
                  onClick={handleInstagramConnect}
                >
                  {t('view.sections.btn_connect_ig')} <ArrowRight size={18} />
                </Button>
              </div>
            )}

            {/* Compensation card */}
            {isAccepted && <PaidCollabPaymentStatus collabId={id} role="creator" />}
            <div className={styles['info-card']}>
              <header className={styles['sidebar-card-header']}>
                <div className={styles['sidebar-icon']}>
                  <Gem size={20} />
                </div>
                <div>
                  <h4>{t('view.sections.compensation')}</h4>
                  <p>{t('view.sections.incentive')}</p>
                </div>
              </header>
              <div className={styles['compensation-value']}>
                <div className={styles['compensation-type']}>
                  {cashCompensation > 0
                    ? exchangeValue > 0
                      ? t('view.sections.hybrid_collab')
                      : t('view.sections.paid_collab')
                    : t('view.sections.exchange_collab')}
                </div>
                {exchangeValue > 0 && (
                  <div className={styles['compensation-row']}>
                    <span>{t('view.sections.exchange_value')}</span>
                    <strong>{compensationCurrency} {exchangeValue.toLocaleString()}</strong>
                  </div>
                )}
                {cashCompensation > 0 && (
                  <div className={styles['compensation-row']}>
                    <span>{t('view.sections.wallet_payment')}</span>
                    <strong>{compensationCurrency} {cashCompensation.toLocaleString()}</strong>
                  </div>
                )}
                {exchangeValue > 0 && cashCompensation > 0 && (
                  <div className={styles['compensation-total']}>
                    <span>{t('view.sections.total_creator_value')}</span>
                    <strong>{compensationValue}</strong>
                  </div>
                )}
                {cashCompensation > 0 && <p>{t('view.sections.wallet_payment_help')}</p>}
              </div>
            </div>

            {/* VIP support card */}
            <div className={styles['info-card']}>
              <header className={styles['sidebar-card-header']}>
                <div className={styles['sidebar-icon']}>
                  <MessageCircle size={16} />
                </div>
                <h4>{t('view.sections.vip_assistance')}</h4>
              </header>
              <p className={styles['sidebar-copy']}>
                {t('view.sections.vip_desc')}
              </p>
              <Button variant="outline" size="sm" fullWidth className={styles['support-button']}>
                {t('view.sections.contact_support')}
              </Button>
            </div>

            {!hasApplied && (
              <div className={`${styles['info-card']} ${styles['before-apply-card']}`}>
                <p className={styles['eyebrow']}>{t('view.sections.before_apply', 'Before applying')}</p>
                <h4>{t('view.sections.review_brief', 'Review the full brief')}</h4>
                <p>{t('view.sections.before_apply_desc', 'After applying, this collab moves into your application flow. Deliverables and reporting are handled only after acceptance.')}</p>
              </div>
            )}

          </aside>
        </div>
      </div>
    </div>
  );
};

export default CreatorOfferView;
